import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Popup from "reactjs-popup";
import Tippy from '@tippyjs/react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import BudgetLineItemDetailsPane from './BudgetLineItemDetailsPane';
import InlineRowToggles from './InlineRowToggles';
import CalcTypeQuantityInput from './CalcTypeQuantityInput';
import { normalizeBudgetFieldValue, isEmptyBudgetField, forecastFollowsOffer, formatAmountForDisplay } from '../utils/budgetFieldValue';
import { KIND_ADMISSION_TAX, canMarkAsAdmissionTax } from '../utils/admissionTax';
import { canToggleTaxWithholdingExemption } from '../../../shared/taxWithholdingExemption';
import { FinancialSummaryMath } from '../../FinancialSummaryMath/FinancialSummaryMath';
import { createTabIndexCalculator, createBudgetLineItemColumns } from '../utils/tabIndexHelper';
import useIsMobile from '../../shared/hooks/useIsMobile';
import { useDebouncedPerKeySave } from '../hooks/useDebouncedPerKeySave';
import useFieldDraft from '../hooks/useFieldDraft';
import { toastError } from '../../../shared/toastHelper';

const axios = require('axios').default;

const NO_ARTIST_WARNING_WIDTH = 24;
const showsNoArtistWarning = (lineItemsKey, budgetLineItem) => (
  lineItemsKey === 'addition_line_items' && !budgetLineItem.performer_id && !budgetLineItem.isAutoGenerated
);

// Mirrors V3BudgetLineItem::RESERVED_ANCILLARY_INCOME_NAMES (keep in sync).
const RESERVED_ANCILLARY_INCOME_NAMES = [
  "Ticketing fee royalties",
  "Add-ons",
  "Donations"
];

const restrictToVerticalAxis = ({ transform, containerNodeRect, draggingNodeRect }) => {
  const clampedTransform = { ...transform, x: 0 };
  if (containerNodeRect && draggingNodeRect) {
    const topBound = containerNodeRect.top - draggingNodeRect.top;
    const bottomBound = containerNodeRect.top + containerNodeRect.height - draggingNodeRect.top - draggingNodeRect.height;
    clampedTransform.y = Math.min(Math.max(clampedTransform.y, topBound), bottomBound);
  }
  return clampedTransform;
};

const dragHandleStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '14px',
  flexShrink: 0,
  cursor: 'grab',
  color: 'var(--bk-c-717680, #717680)',
  fontSize: '12px',
};

const SortableRow = ({ id, disabled, children, style }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled });

  const sortableStyle = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={sortableStyle} {...attributes}>
      {!disabled && (
        <div style={dragHandleStyle} {...listeners}>
          <i className="far fa-grip-dots-vertical"></i>
        </div>
      )}
      {disabled && <div style={{ width: '14px', flexShrink: 0 }} />}
      {children}
    </div>
  );
};

const inlineMenuContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  width: "215px",
  padding: "0px",
  zIndex: 20
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px'
};

const sectionTitleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--bk-c-333333, #333333)',
  margin: 0
};

const contentContainerStyle = {
  display: 'flex',
  gap: '8px',
  padding: '0 16px 16px 16px',
  overflowX: 'auto'
};

const collapseButtonStyle = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '10px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none'
};

const inputLabelStyle = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--bk-c-414651, #414651)',
  marginBottom: '0',
  display: 'block'
};

const inputStyle = {
  backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
  border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
  borderRadius: '0',
  padding: '8px 8px',
  fontSize: '14px',
  color: 'var(--bk-c-333333, #333333)',
  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
  width: '100%',
  boxSizing: 'border-box'
};

const disabledInputStyle = {
  ...inputStyle,
  backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
  color: 'var(--bk-c-717680, #717680)'
};

const convertedTotalStyle = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'var(--bk-c-717680, #717680)',
  textAlign: 'right',
  paddingRight: '8px'
};

const addButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  color: 'var(--bk-c-136395, #136395)',
  fontSize: '14px',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: '8px 0',
  outline: 'none',
  width: 'fit-content',
};

const verticalTextStyle = {
  writingMode: 'vertical-rl',
  textOrientation: 'mixed',
  fontSize: '16px',
  fontWeight: '600',
  color: 'var(--bk-c-333333, #333333)',
  textAlign: 'center',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const columnHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 0'
};

const columnTitleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: 'var(--bk-c-333333, #333333)',
  paddingLeft: '8px'
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
    borderRadius: '0',
    boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
    minHeight: '36px',
    fontSize: '14px'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontSize: '14px'
  }),
  option: (provided) => ({
    ...provided,
    fontSize: '14px'
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px'
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: 'var(--bk-c-9ca3af, #9CA3AF)'
  })
};

export const CALC_TYPE_OPTIONS = {
  'Other Income': [
    { value: 'fixed', label: 'Fixed' },
    { value: 'per_tickets_sold', label: 'Per Tickets Sold' }
  ],
  'Ancillary Income': [
    { value: 'fixed', label: 'Fixed' },
    { value: 'per_tickets_sold', label: 'Per Tickets Sold' }
  ],
  'Before Adjusted Deductions': [
    { value: 'fixed', label: 'Fixed' },
    { value: 'per_tickets_sold', label: 'Per Tickets Sold' },
    { value: 'percentage_of_gross', label: '% of Gross' },
    { value: 'tax_inclusive_percentage', label: '% of Gross (Tax-Inclusive)' },
    { value: 'percentage_of_net_gross', label: '% of Net Gross (Multiplier)' },
    { value: 'tax_inclusive_percentage_of_net_gross', label: '% of Net Gross (Divisor)' }
  ],
  'Additions': [
    { value: 'fixed', label: 'Fixed' },
    { value: 'per_tickets_sold', label: 'Per Tickets Sold' },
    { value: 'percentage_of_gross', label: '% of Gross' },
    { value: 'percentage_of_net_gross', label: '% of Net Gross (Multiplier)' },
    { value: 'percentage_of_net', label: '% of Net' },
    { value: 'percentage_of_expenses', label: '% of Expenses' }
  ],
  'Expenses': [
    { value: 'fixed', label: 'Fixed' },
    { value: 'per_tickets_sold', label: 'Per Tickets Sold' },
    { value: 'percentage_of_gross', label: '% of Gross' },
    { value: 'percentage_of_net_gross', label: '% of Net Gross (Multiplier)' },
    { value: 'percentage_of_net', label: '% of Net' },
    { value: 'percentage_of_expenses', label: '% of Expenses' }
  ],
  'Ancillary Expenses': [
    { value: 'fixed', label: 'Fixed' },
    { value: 'per_tickets_sold', label: 'Per Tickets Sold' },
    { value: 'percentage_of_gross', label: '% of Gross' },
    { value: 'percentage_of_net_gross', label: '% of Net Gross (Multiplier)' },
    { value: 'percentage_of_net', label: '% of Net' },
    { value: 'percentage_of_expenses', label: '% of Expenses' }
  ]
};

const inlineRowIconBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: '6px',
  cursor: 'pointer',
  color: 'var(--bk-c-6b7280, #6B7280)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const inlineRowIconDangerBtnStyle = {
  ...inlineRowIconBtnStyle,
  color: 'var(--bk-c-d92d20, #D92D20)',
};

const LineItemRowMenu = ({
  offer,
  showEditDetails,
  budgetLineItem,
  onDeleteRow,
  onEditDetails,
  allowOfferVisibilityToggle = true,
  sectionLineItems = null,
  onToggleFlag
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        <InlineRowToggles
          budgetLineItem={budgetLineItem}
          offer={offer}
          showVisibilityToggle={allowOfferVisibilityToggle && !budgetLineItem.isReservedAncillaryIncome}
          sectionLineItems={sectionLineItems}
          onToggle={(flag, nextValue) => onToggleFlag(budgetLineItem, flag, nextValue)}
        />
        {showEditDetails && (
          <button
            type="button"
            aria-label="Edit Details"
            onClick={(e) => { e.preventDefault(); onEditDetails(budgetLineItem); }}
            style={inlineRowIconBtnStyle}
          >
            <i className="far fa-edit" />
          </button>
        )}
        <button
          type="button"
          aria-label="Delete row"
          onClick={(e) => { e.preventDefault(); onDeleteRow(budgetLineItem); }}
          style={inlineRowIconDangerBtnStyle}
        >
          <i className="far fa-times-circle" />
        </button>
      </div>
    );
  }

  return (
    <Popup
      trigger={
        <a href="#" className="text-muted">
          <i className="fas fa-ellipsis-v"></i>
        </a>
      }
      onOpen={(e) => e.preventDefault()}
      arrow={false}
      offsetY={5}
      position="bottom right"
      contentStyle={inlineMenuContentStyle}
      closeOnDocumentClick
    >
      {close => (
        <div>
          <div className="row text-left">
            <div className="col">
              <ul className="list-group offer-inline-menu highlight-primary">
                {allowOfferVisibilityToggle && !budgetLineItem.isReservedAncillaryIncome && (
                  budgetLineItem.show_in_offer ? (
                    <li className="list-group-item">
                      <a href="#"
                         className="text-dark"
                         onClick={(e) => {
                           e.preventDefault();
                           onToggleFlag(budgetLineItem, 'show_in_offer', false);
                           close();
                         }}>
                        <i className="text-muted fas fa-eye-slash fa-fw mx-2"></i>
                        <span className="font-weight-normal">
                          Don't show in offer
                        </span>
                      </a>
                    </li>
                  ) : (
                    <li className="list-group-item">
                      <a href="#"
                         className="text-dark"
                         onClick={(e) => {
                           e.preventDefault();
                           onToggleFlag(budgetLineItem, 'show_in_offer', true);
                           close();
                         }}>
                        <i className="text-muted fas fa-eye fa-fw mx-2"></i>
                        <span className="font-weight-normal">
                          Show in offer
                        </span>
                      </a>
                    </li>
                  )
                )}
                {budgetLineItem.category === 'EXPENSES' && offer?.revenue_base === 'adjusted_gross' && (
                  <li className="list-group-item border-0">
                    <a href="#"
                       className="text-dark"
                       onClick={(e) => {
                         e.preventDefault();
                         onToggleFlag(budgetLineItem, 'deducted_before_split',
                           !budgetLineItem.deducted_before_split);
                         close();
                       }}>
                      <i className={`text-muted ${budgetLineItem.deducted_before_split ? 'fas fa-check-square' : 'far fa-square'} fa-fw mx-2`}></i>
                      <span className="font-weight-normal">
                        Deducted before split
                      </span>
                    </a>
                  </li>
                )}
                {showEditDetails && (
                  <li className="list-group-item border-0">
                    <a href="#"
                       className="text-dark"
                       onClick={(e) => {
                         e.preventDefault();
                         onEditDetails(budgetLineItem);
                         close();
                       }}>
                      <i className="text-muted far fa-edit fa-fw mx-2"></i>
                      <span className="font-weight-normal">
                        Edit Details
                      </span>
                    </a>
                  </li>
                )}
                <li className="list-group-item delete-offer-li border-0">
                  <a href="#"
                     onClick={(e) => {
                       e.preventDefault();
                       onDeleteRow(budgetLineItem);
                       close();
                     }}
                     className="text-danger">
                    <i className="far fa-times-circle fa-fw mx-2"></i>
                    <span className="font-weight-normal">
                      Delete row
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </Popup>
  );
};

LineItemRowMenu.propTypes = {
  offer: PropTypes.object,
  showEditDetails: PropTypes.bool,
  budgetLineItem: PropTypes.object.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  onEditDetails: PropTypes.func.isRequired,
  allowOfferVisibilityToggle: PropTypes.bool,
  sectionLineItems: PropTypes.array,
  onToggleFlag: PropTypes.func.isRequired,
};

const BudgetLineItemSection = ({
  csrfToken,
  team,
  showEditDetails = true,
  offer,
  budget,
  calculated,
  updateBudget,
  // Only Before Adjusted Deductions passes this; other sections never mark a row.
  onPassThroughChange,
  createBudgetLineItem,
  deleteBudgetLineItem,
  autoSaveBudgetLineItem,
  formatCalculatedNumber,
  expanded,
  onToggleExpand,
  sectionTitle,
  sectionSubtitle,
  lineItemsKey,
  lineItemsFilter,
  calculatedSectionKey,
  calculatedTotalSectionKey,
  calculatedTotalKey = 'total',
  calculatedForecastTotalKey = 'forecastTotal',
  calculatedActualTotalKey = 'actualTotal',
  calculatedRightTotalKey = 'rightTotal',
  calculatedRightForecastTotalKey = 'rightForecastTotal',
  calculatedRightActualTotalKey = 'rightActualTotal',
  addButtonText,
  hasOfferColumn = true,
  showOfferColumn = true,
  showForecastColumn = true,
  showActualsColumn = true,
  hideCollapsedColumns = false,
  // Sections that partition their rows by show_in_offer (Expenses/Ancillary
  // Expenses, Other/Ancillary Income) suppress the per-row "show in offer"
  // toggle: flipping it there relocates the row to the sibling card, which
  // reads as a delete. The dedicated Add buttons set membership instead.
  allowOfferVisibilityToggle = true,
  // Separate from allowOfferVisibilityToggle, which governs the mobile sheet and
  // kebab item. Only Expenses and Ancillary Expenses had this eye before
  // 4b2e157a0a routed it through that prop and dropped it by accident; Before
  // Adjusted Deductions never had one and stays without. (O-4748)
  showInlineVisibilityToggle = false,
  showInlineRowToggles = false,
  onColumnVisibilityChange,
  financeCategories = [],
  onFinanceCategoryAdded,
  performers = [],
  sectionTabIndexOffset = 0,
  showCurrencyConversion,
  globalCurrency,
  actualsItems = {},
  reorderLineItem,
  onReorderLineItems,
  allLineItems: allLineItemsFromContext,
  // Read-only rows synthesized from sibling V3Offers — passed only to the
  // Expenses section by ExpensesSection. Each row carries
  // isAutoGenerated: true so all existing locked-input branches apply, and
  // isSiblingGuarantee: true so we can layer on the link-arrow icon and
  // click-to-navigate handler. See utils/siblingGuaranteeExpenses.js
  // (O-2993).
  siblingRows = [],
  onSelectSiblingOffer
}) => {
  const isMobile = useIsMobile();
  const [detailsPaneBudgetLineItem, setDetailsPaneBudgetLineItem] = useState(null);
  const [categoryDropdownOpenId, setCategoryDropdownOpenId] = useState(null);
  const [categoryDropdownPos, setCategoryDropdownPos] = useState(null);
  const categoryIconRefs = useRef({});
  const categoryDropdownRef = useRef(null);

  const showOffer = showOfferColumn;
  const showForecast = showForecastColumn;
  const showActuals = showActualsColumn;

  const autoSaveRef = useRef(autoSaveBudgetLineItem);
  useEffect(() => {
    autoSaveRef.current = autoSaveBudgetLineItem;
  }, [autoSaveBudgetLineItem]);

  const debouncedAutoSave = useDebouncedPerKeySave(autoSaveBudgetLineItem);

  const {
    isCleared, zeroFocus: handleZeroFocus, release: releaseClearedField,
    displayValue, handleFocus: handleAmountFocus, handleBlur, handleInput,
  } = useFieldDraft();

  const amountFormat = (calcType) => (isPercentageCalcType(calcType) ? (value) => value : formatAmountForDisplay);

  const amountDisplayValue = (item, fieldPrefix, displayedValue, calcType) =>
    displayValue(item, fieldPrefix, displayedValue, amountFormat(calcType));

  const handleAmountBlur = (item, fieldPrefix, commitZero = false) => () => {
    handleBlur(item, fieldPrefix);
    // Offer column only: a field left blank falls back to 0 so nothing is left
    // empty. Forecast/Actual stay blank, which is how they keep inheriting.
    if (commitZero && isEmptyBudgetField(item[`left_hand${fieldPrefix}`])) {
      handleAmountFieldChange(item, fieldPrefix, item[`left_hand${fieldPrefix}`], 'blur');
    }
  };

  const handleQtyBlur = (item, fieldPrefix, commitZero = false) => () => {
    releaseClearedField(item, `qty${fieldPrefix}`);
    if (commitZero && isEmptyBudgetField(item[`right_hand${fieldPrefix}`])) {
      handleQtyFieldChange(item, fieldPrefix, item[`right_hand${fieldPrefix}`], 'blur');
    }
  };

  const allLineItems = budget[lineItemsKey] || [];
  const filteredLineItems = lineItemsFilter
    ? allLineItems.filter(lineItemsFilter)
    : allLineItems;

  // Check if an item is a reserved ancillary income item
  const isReservedAncillaryIncomeItem = (item) => {
    return item.show_in_offer === false &&
           RESERVED_ANCILLARY_INCOME_NAMES.includes(item.name);
  };

  // Merge actuals data with budget line items
  // actualsData is a hash keyed by item name, e.g. { "Add-ons": { left_hand_actual: 100, right_hand_actual: 5 } }
  const mergeWithActualsItems = (budgetItems, actualsData) => {
    return budgetItems.map(item => {
      const isReserved = isReservedAncillaryIncomeItem(item);
      const actualsForItem = actualsData && actualsData[item.name];

      return {
        ...item,
        // Mark reserved ancillary income items so their actuals can't be manually edited
        isReservedAncillaryIncome: isReserved,
        // Apply actuals from the backend if available for this item
        ...(actualsForItem ? {
          left_hand_actual: actualsForItem.left_hand_actual,
          right_hand_actual: actualsForItem.right_hand_actual
        } : {})
      };
    });
  };

  const mergedLineItems = mergeWithActualsItems(filteredLineItems, actualsItems);

  // Get bonus items from calculated additions section that aren't in the budget's line items
  // Bonuses are auto-generated when ticket count thresholds are met
  const getBonusItems = () => {
    // Only show bonus items in the Additions section
    if (calculatedSectionKey !== 'additionsSection') {
      return [];
    }
    if (!calculated || !calculated[calculatedSectionKey]) {
      return [];
    }
    const calculatedItems = calculated[calculatedSectionKey].items || [];
    // Get UUIDs of items that are already in the budget's addition line items
    const budgetItemUuids = new Set(allLineItems.map(item => item.uuid));
    // Filter to bonus items only:
    // - No uuid (auto-generated, not from budget)
    // - Not a lift item (lift !== true)
    // - Name starts with "Bonus for" (how bonuses are named in SettlementMath)
    return calculatedItems
      .filter(item => !item.uuid && !item.lift && item.name?.startsWith('Bonus for '))
      .map(item => ({
        ...item,
        uuid: `bonus-${item.overridable_id || item.name}`,
        isAutoGenerated: true
      }));
  };

  const bonusItems = getBonusItems();
  // Sibling guarantee rows render at the top of the Expenses table, above
  // manually-entered items (O-2993). They carry isAutoGenerated: true, so
  // they're already excluded from draggableItems / sortableIds below.
  const lineItems = [...siblingRows, ...mergedLineItems, ...bonusItems];

  const updatedBudgetWithBudgetLineItem = (budget, budgetLineItem) => {
    const updatedItems = [...budget[lineItemsKey]].map((item) => {
      if (item.uuid === budgetLineItem.uuid) {
        return budgetLineItem;
      }
      return item;
    });
    return { ...budget, [lineItemsKey]: updatedItems };
  };

  const handleToggleFlag = (budgetLineItem, flag, nextValue) => {
    const updatedBudgetLineItem = { ...budgetLineItem, [flag]: nextValue };
    const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
    updateBudget(updatedBudget);
    debouncedAutoSave(csrfToken, team, budgetLineItem, { [flag]: nextValue }, { flush: true });

    // The row PATCH above is the only write: V3BudgetLineItem syncs the flag onto
    // every offer sharing this budget, because the marker is budget-scoped and the
    // flag is not. This mirrors it locally so the Deal Terms toggle moves with the
    // click instead of waiting for a refetch. (O-4778)
    if (flag === 'kind' && onPassThroughChange) {
      onPassThroughChange(nextValue === KIND_ADMISSION_TAX);
    }
  };

  const updatedBudgetRemovingBudgetLineItem = (budget, budgetLineItem) => {
    const updatedItems = budget[lineItemsKey].filter((item) =>
      item.uuid !== budgetLineItem.uuid
    );
    return { ...budget, [lineItemsKey]: updatedItems };
  };

  // O-4509: the passed-through admission tax IS a before-adjusted row, but it is
  // added on top rather than deducted, so it can't sit inside the section Total.
  // It renders as its own line beneath each column's Total, in that column, so
  // it is obvious which of offer / forecast / actuals it belongs to.
  const passThroughTaxFor = (mode) => {
    if (calculatedSectionKey !== 'beforeAdjustedDeductionsSection') return 0;
    const summary = calculated?.showSummary;
    if (!summary) return 0;
    if (mode === 'forecast') return summary.admissionTaxForecast || 0;
    if (mode === 'actual') return summary.admissionTaxActual || 0;
    return summary.admissionTax || 0;
  };

  const renderPassThroughTaxRow = (mode) => {
    const amount = passThroughTaxFor(mode);
    if (!amount) return null;
    const labelStyle = {
      width: calcTypeQuantityWidth + amountWidth, marginRight: '-1px', textAlign: 'right',
      fontSize: '13px', paddingRight: '8px', color: 'var(--bk-c-535862, #535862)',
    };
    const valueStyle = {
      width: totalWidth, textAlign: 'right', fontSize: '13px', paddingRight: '8px',
      color: 'var(--bk-c-535862, #535862)',
      marginRight: showCurrencyConversion ? '-1px' : 0,
    };
    return (
      <div data-testid={`pass-through-tax-${mode}`}
           style={{ display: 'flex', height: rowHeight, alignItems: 'center' }}>
        <div style={labelStyle}>+ Admission Tax</div>
        <div style={valueStyle}>{formatCalculatedNumber(amount)}</div>
        {showCurrencyConversion && globalCurrency && (
          <div style={{ ...valueStyle, width: convertedAmountWidth, marginRight: 0 }}>
            {globalCurrency.formatted(amount)}
          </div>
        )}
      </div>
    );
  };

  const totalSectionKey = calculatedTotalSectionKey || calculatedSectionKey;

  const getTotal = () => {
    if (calculated && Object.keys(calculated).length > 0 &&
        calculated[totalSectionKey]) {
      return calculated[totalSectionKey][calculatedTotalKey] || 0;
    }
    return 0;
  };

  const getForecastTotal = () => {
    if (calculated && Object.keys(calculated).length > 0 &&
        calculated[totalSectionKey]) {
      return calculated[totalSectionKey][calculatedForecastTotalKey] || 0;
    }
    return 0;
  };

  const getActualsTotal = () => {
    if (calculated && Object.keys(calculated).length > 0 &&
        calculated[totalSectionKey]) {
      return calculated[totalSectionKey][calculatedActualTotalKey] || 0;
    }
    return 0;
  };

  const getRightTotal = () => {
    if (calculated && Object.keys(calculated).length > 0 &&
        calculated[calculatedSectionKey]) {
      return calculated[calculatedSectionKey][calculatedRightTotalKey] || 0;
    }
    return 0;
  };

  const getRightForecastTotal = () => {
    if (calculated && Object.keys(calculated).length > 0 &&
        calculated[calculatedSectionKey]) {
      return calculated[calculatedSectionKey][calculatedRightForecastTotalKey] || 0;
    }
    return 0;
  };

  const getRightActualsTotal = () => {
    if (calculated && Object.keys(calculated).length > 0 &&
        calculated[calculatedSectionKey]) {
      return calculated[calculatedSectionKey][calculatedRightActualTotalKey] || 0;
    }
    return 0;
  };

  const getCalculatedRow = (budgetLineItem) => {
    if (calculated && Object.keys(calculated).length > 0) {
      if (calculatedSectionKey === 'expensesSection' &&
          FinancialSummaryMath.isFinalExpenseItem(budgetLineItem) &&
          calculated.finalExpensesSection) {
        const items = calculated.finalExpensesSection.items || [];
        return items.find((item) => item.uuid === budgetLineItem.uuid);
      }

      // O-4509: while "tax after facility fee" is on, the tax row is totalled
      // in admissionTaxSection rather than the deductions section it still
      // renders in. Without this it finds no calculated row and falls back to
      // the raw values — a percentage row shows qty 0 and a $0 total.
      if (calculatedSectionKey === 'beforeAdjustedDeductionsSection' && calculated.admissionTaxSection) {
        const taxItems = calculated.admissionTaxSection.items || [];
        const taxMatch = taxItems.find((item) => item.uuid === budgetLineItem.uuid);
        if (taxMatch) return taxMatch;
      }

      if (calculated[calculatedSectionKey]) {
        const items = calculated[calculatedSectionKey].items || [];
        return items.find((item) => item.uuid === budgetLineItem.uuid);
      }
    }
    return null;
  };

  const handleAddRow = () => {
    // Additions need a performer or they don't appear on any offer; when the
    // offer has exactly one, assign it automatically (O-4320). Ambiguous (0 or
    // 2+) stays unassigned and shows the warning for manual assignment.
    const solePerformerId = lineItemsKey === 'addition_line_items' && performers.length === 1
      ? performers[0].id
      : null;
    createBudgetLineItem(csrfToken, team, budget, (filteredLineItems.length + 1), solePerformerId);
  };

  const handleDeleteRow = (budgetLineItem) => {
    const updated = updatedBudgetRemovingBudgetLineItem(budget, budgetLineItem);
    updateBudget(updated);
    deleteBudgetLineItem(csrfToken, team, budgetLineItem);
  };

  const handleFieldChange = (budgetLineItem, field, value, immediate = false, related = {}) => {
    const changes = { [field]: value };
    if (field === 'calc_type') {
      changes.operator = FinancialSummaryMath.operatorForCalcType(value);
    }
    const updatedBudgetLineItem = { ...budgetLineItem, ...changes, ...related };

    const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
    updateBudget(updatedBudget);

    if (immediate) {
      autoSaveRef.current(csrfToken, team, updatedBudgetLineItem);
    } else {
      debouncedAutoSave(csrfToken, team, budgetLineItem, changes);
    }
  };

  const handleAmountFieldChange = (budgetLineItem, fieldPrefix, value, mode = 'edit') => {
    // Ends at the first keystroke, not blur: while held the input renders '', so
    // every render re-blanks it and each onChange carries only the character just
    // typed — "123" landed as "3".
    releaseClearedField(budgetLineItem, fieldPrefix);
    const leftHandField = `left_hand${fieldPrefix}`;
    const changes = { [leftHandField]: normalizeBudgetFieldValue(value, mode) };
    const updatedBudgetLineItem = { ...budgetLineItem, ...changes };

    const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
    updateBudget(updatedBudget);
    debouncedAutoSave(csrfToken, team, budgetLineItem, changes);
  };

  const handleAmountInput = (budgetLineItem, fieldPrefix, displayedValue, calcType) =>
    handleInput(budgetLineItem, fieldPrefix, displayedValue, amountFormat(calcType),
      (typed) => handleAmountFieldChange(budgetLineItem, fieldPrefix, typed));

  const handleQtyFieldChange = (budgetLineItem, fieldPrefix, value, mode = 'edit') => {
    releaseClearedField(budgetLineItem, `qty${fieldPrefix}`);
    const rightHandField = `right_hand${fieldPrefix}`;
    const normalized = normalizeBudgetFieldValue(value, mode);
    const changes = { [rightHandField]: normalized };
    // Editing the offer qty carries a following forecast qty along so the
    // forecast mirrors the offer (O-4320), like the sale-price sync on tickets.
    if (fieldPrefix === '' && forecastFollowsOffer(budgetLineItem.right_hand_forecast, budgetLineItem.right_hand)) {
      changes.right_hand_forecast = normalized;
    }
    const updatedBudgetLineItem = { ...budgetLineItem, ...changes };

    const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
    updateBudget(updatedBudget);
    debouncedAutoSave(csrfToken, team, budgetLineItem, changes);
  };

  // Handler for per-column calc_type changes with cascading behavior
  const handleCalcTypeChange = (budgetLineItem, column, newValue) => {
    let updates = { ...budgetLineItem };
    const newOperator = FinancialSummaryMath.operatorForCalcType(newValue);

    if (column === 'offer') {
      updates.calc_type = newValue;
      updates.operator = newOperator;
      // Cascade to forecast if it was inheriting (null/undefined)
      if (!budgetLineItem.calc_type_forecast) {
        // Forecast is inheriting, so actual also inherits through forecast
        // No explicit update needed - the effective calc type methods will handle it
      }
    } else if (column === 'forecast') {
      updates.calc_type_forecast = newValue;
      if (!updates.left_hand_forecast && updates.left_hand_forecast !== 0) {
        updates.left_hand_forecast = 0;
      }
    } else if (column === 'actual') {
      updates.calc_type_actual = newValue;
      if (!updates.left_hand_actual && updates.left_hand_actual !== 0) {
        updates.left_hand_actual = 0;
      }
    }

    const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updates);
    updateBudget(updatedBudget);
    autoSaveRef.current(csrfToken, team, updates);
  };

  // Get the effective calc_type for a column (mirrors backend logic)
  const getEffectiveCalcType = (budgetLineItem, column) => {
    if (column === 'offer') {
      return budgetLineItem.calc_type || 'fixed';
    } else if (column === 'forecast') {
      return budgetLineItem.calc_type_forecast || budgetLineItem.calc_type || 'fixed';
    } else {
      return budgetLineItem.calc_type_actual ||
             budgetLineItem.calc_type_forecast ||
             budgetLineItem.calc_type || 'fixed';
    }
  };

  const handleNameBlur = (budgetLineItem, index, value) => {
    if (!value || value.length < 1) {
      const defaultName = sectionTitle.replace(/s$/, '') + " #" + (index + 1);
      const updatedBudgetLineItem = {
        ...budgetLineItem,
        name: defaultName
      };
      const updatedBudget = updatedBudgetWithBudgetLineItem(budget, updatedBudgetLineItem);
      updateBudget(updatedBudget);
      autoSaveBudgetLineItem(csrfToken, team, updatedBudgetLineItem);
    }
  };

  const parseNumber = (value) => {
    if (typeof value === 'string') {
      return value.length > 0 ? value.replace(/,/g, '') : '';
    }
    return value;
  };

  // Drag and drop reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const draggableItems = lineItems.filter(li => !li.isAutoGenerated && !li.isReservedAncillaryIncome);
  const sortableIds = draggableItems.map(li => li.uuid || String(li.id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id || !reorderLineItem) return;

    const oldIndex = draggableItems.findIndex(li => (li.uuid || String(li.id)) === active.id);
    const newIndex = draggableItems.findIndex(li => (li.uuid || String(li.id)) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(draggableItems, oldIndex, newIndex);
    const movedItem = reordered[newIndex];

    // Optimistic update: assign new positions to reordered items in the full context array
    const reorderedPositions = {};
    reordered.forEach((item, i) => { reorderedPositions[item.uuid] = i + 1; });

    const updatedAllLineItems = (allLineItemsFromContext || []).map(li => {
      if (reorderedPositions[li.uuid] !== undefined) {
        return { ...li, position: reorderedPositions[li.uuid] };
      }
      return li;
    });

    onReorderLineItems(updatedAllLineItems);

    // Persist to backend — acts_as_list handles repositioning other items
    reorderLineItem(movedItem.id, newIndex + 1);
  };

  const loadFinanceCategories = (inputValue, callback) => {
    axios.get(`/teams/${team.id}/finance_categories/autocomplete`, {
      params: { name: inputValue }
    })
    .then(response => {
      callback(response.data);
    })
    .catch(error => {
      console.error('Failed to load finance categories:', error);
      callback([]);
    });
  };

  const createFinanceCategory = async (inputValue) => {
    try {
      const response = await axios.post(
        `/teams/${team.id}/finance_categories`,
        { finance_category: { name: inputValue } },
        { headers: { 'X-CSRF-TOKEN': csrfToken } }
      );
      return { value: response.data.id, label: response.data.name };
    } catch (error) {
      console.error('Failed to create finance category:', error);
      toastError(`Could not create category "${inputValue}"`);
      return null;
    }
  };

  const isPercentageCalcType = (calcType) => {
    return ['percentage_of_gross', 'percentage_of_net_gross',
            'percentage_of_net', 'percentage_of_expenses',
            'tax_inclusive_percentage', 'tax_inclusive_percentage_of_net_gross'].includes(calcType);
  };

  const columnBoxStyle = {
    backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
    border: '1px solid var(--bk-c-f5f5f5, #F5F5F5)',
    borderRadius: '8px',
    padding: '0 8px 8px 8px',
    flexShrink: 0
  };

  const collapsedColumnStyle = {
    borderRadius: '8px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    width: '56px',
    cursor: 'pointer'
  };

  const offerCollapsedStyle = {
    ...collapsedColumnStyle,
    backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
    border: '1px solid var(--bk-c-f5f5f5, #F5F5F5)'
  };

  const forecastCollapsedStyle = {
    ...collapsedColumnStyle,
    backgroundColor: 'var(--bk-c-f6fafe, #F6FAFE)',
    border: '1px solid var(--bk-c-e4f2fc, #E4F2FC)'
  };

  const actualsCollapsedStyle = {
    ...collapsedColumnStyle,
    backgroundColor: 'var(--bk-c-eff4ff, #EFF4FF)',
    border: '1px solid var(--bk-c-d1e0ff, #D1E0FF)'
  };

  const forecastExpandedStyle = {
    ...columnBoxStyle,
    backgroundColor: 'var(--bk-c-f6fafe, #F6FAFE)',
    border: '1px solid var(--bk-c-e4f2fc, #E4F2FC)'
  };

  const actualsExpandedStyle = {
    ...columnBoxStyle,
    backgroundColor: 'var(--bk-c-eff4ff, #EFF4FF)',
    border: '1px solid var(--bk-c-d1e0ff, #D1E0FF)'
  };

  const rowHeight = 36;
  const headerHeight = 48;
  const labelRowHeight = 18;
  const labelMarginBottom = 6;
  const totalHeaderHeight = headerHeight + labelRowHeight + labelMarginBottom;

  const calcTypeQuantityWidth = 116;
  const amountWidth = 92;
  const totalWidth = 105;
  const convertedAmountWidth = 105;

  const calcTypeOptions = CALC_TYPE_OPTIONS[sectionTitle] || CALC_TYPE_OPTIONS['Other Income'];

  const baseValuePreviews = useMemo(() => {
    if (!calculated || Object.keys(calculated).length === 0) return {};

    const makeForMode = (mode) => {
      const isOffer = mode === 'offer';
      const isForecast = mode === 'forecast';
      const totalKey = isOffer ? 'total' : isForecast ? 'forecastTotal' : 'actualTotal';
      const extTotalKey = isOffer ? 'externalTotal' : isForecast ? 'externalForecastTotal' : 'externalActualTotal';
      const rightTotalKey = isOffer ? 'rightTotal' : isForecast ? 'rightForecastTotal' : 'rightActualTotal';
      const summaryKey = isOffer ? '' : isForecast ? 'Forecast' : 'Actual';

      const grossPreview = (calculated.ticketsSection?.[totalKey] || 0) +
        (calculated.revenueSection?.[extTotalKey] || 0);

      const netGrossPreview = calculated.showSummary?.[`netBoxOfficeReceipts${summaryKey}`] || 0;

      return {
        per_tickets_sold: calculated.ticketsSection?.[rightTotalKey] || 0,
        percentage_of_gross: grossPreview,
        tax_inclusive_percentage: grossPreview,
        percentage_of_net_gross: netGrossPreview,
        tax_inclusive_percentage_of_net_gross: netGrossPreview,
        percentage_of_net: calculated.showSummary?.[`showNetAfterExpenses${summaryKey}`] || 0,
        percentage_of_expenses: calculated.expensesSection?.[extTotalKey] || 0,
      };
    };

    return { offer: makeForMode('offer'), forecast: makeForMode('forecast'), actual: makeForMode('actual') };
  }, [calculated]);

  // Whether category icon is shown on line items
  const showCategoryColumn = financeCategories.length > 0 || sectionTitle !== 'Ancillary Income';

  const getCategoryLabel = (budgetLineItem) => {
    if (!budgetLineItem.finance_category_id) return null;
    return budgetLineItem.finance_category?.name ||
      financeCategories.find(c => c.id === budgetLineItem.finance_category_id)?.name || null;
  };

  const updateCategoryDropdownPos = useCallback(() => {
    if (categoryDropdownOpenId && categoryIconRefs.current[categoryDropdownOpenId]) {
      const rect = categoryIconRefs.current[categoryDropdownOpenId].getBoundingClientRect();
      setCategoryDropdownPos({
        top: rect.bottom + 4,
        left: Math.max(rect.right - 250, 8),
      });
    }
  }, [categoryDropdownOpenId]);

  const handleCategoryClickOutside = useCallback((e) => {
    if (
      categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target) &&
      categoryIconRefs.current[categoryDropdownOpenId] &&
      !categoryIconRefs.current[categoryDropdownOpenId].contains(e.target)
    ) {
      setCategoryDropdownOpenId(null);
    }
  }, [categoryDropdownOpenId]);

  useEffect(() => {
    if (categoryDropdownOpenId) {
      updateCategoryDropdownPos();
      document.addEventListener('mousedown', handleCategoryClickOutside);
      window.addEventListener('scroll', updateCategoryDropdownPos, true);
      return () => {
        document.removeEventListener('mousedown', handleCategoryClickOutside);
        window.removeEventListener('scroll', updateCategoryDropdownPos, true);
      };
    }
  }, [categoryDropdownOpenId, handleCategoryClickOutside, updateCategoryDropdownPos]);

  const renderCategoryDropdown = () => {
    if (!categoryDropdownOpenId || !categoryDropdownPos) return null;

    const budgetLineItem = lineItems.find(
      li => (li.uuid || String(li.id)) === categoryDropdownOpenId
    );
    if (!budgetLineItem) return null;

    return ReactDOM.createPortal(
      <div
        ref={categoryDropdownRef}
        style={{
          position: 'fixed',
          top: categoryDropdownPos.top,
          left: categoryDropdownPos.left,
          width: 250,
          backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
          border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
          borderRadius: '6px',
          boxShadow: '0px 4px 12px rgba(10, 13, 18, 0.12)',
          zIndex: 9999,
          padding: '8px',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <AsyncCreatableSelect
          cacheOptions
          autoFocus
          menuIsOpen
          defaultOptions={financeCategories.map(c => ({
            value: c.id,
            label: c.name
          }))}
          loadOptions={loadFinanceCategories}
          isValidNewOption={(input, _value, options) => input.trim() !== '' && !options.some(o => o.label.trim().toLowerCase() === input.trim().toLowerCase())}
          onCreateOption={async (inputValue) => {
            const newCategory = await createFinanceCategory(inputValue);
            if (newCategory) {
              onFinanceCategoryAdded?.({ id: newCategory.value, name: newCategory.label });
              handleFieldChange(budgetLineItem, 'finance_category_id', newCategory.value, true, { finance_category: { id: newCategory.value, name: newCategory.label } });
              setCategoryDropdownOpenId(null);
            }
          }}
          onChange={(option) => {
            if (option) onFinanceCategoryAdded?.({ id: option.value, name: option.label });
            handleFieldChange(budgetLineItem, 'finance_category_id', option?.value || null, true, { finance_category: option ? { id: option.value, name: option.label } : null });
            setCategoryDropdownOpenId(null);
          }}
          value={budgetLineItem.finance_category_id ? {
            value: budgetLineItem.finance_category_id,
            label: getCategoryLabel(budgetLineItem) || ''
          } : null}
          placeholder="Search or create..."
          isClearable
          styles={{
            ...selectStyles,
            control: (provided) => ({
              ...provided,
              border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
              borderRadius: '6px',
              boxShadow: 'none',
              minHeight: 36,
              fontSize: '14px',
            }),
            menu: (provided) => ({
              ...provided,
              position: 'relative',
              boxShadow: 'none',
              border: 'none',
              marginTop: '4px',
              fontSize: '14px',
            }),
            menuList: (provided) => ({
              ...provided,
              maxHeight: '200px',
            }),
          }}
        />
      </div>,
      document.body
    );
  };

  const getTabIndex = useMemo(() => createTabIndexCalculator({
    sectionTabIndexOffset,
    columns: createBudgetLineItemColumns({
      hasOfferColumn,
      showOffer,
      showForecast,
      showActuals
    })
  }), [sectionTabIndexOffset, hasOfferColumn, showOffer, showForecast, showActuals]);

  return (
    <div style={{
      backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
      border: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
      borderRadius: '8px',
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      overflow: 'hidden'
    }}>
      <div style={{ ...sectionHeaderStyle, padding: isMobile ? '12px' : sectionHeaderStyle.padding, cursor: 'pointer' }} onClick={onToggleExpand}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={{ ...sectionTitleStyle, fontSize: isMobile ? '16px' : sectionTitleStyle.fontSize }}>{sectionTitle}</h3>
          {sectionSubtitle && (
            <Tippy
              content={sectionSubtitle}
              placement="top"
              theme="material"
              animation="shift-away"
            >
              <i className="fas fa-info-circle" style={{ fontSize: '14px', color: 'var(--bk-c-717680, #717680)', cursor: 'help', marginTop: '2px' }} onClick={(e) => e.stopPropagation()} />
            </Tippy>
          )}
        </div>
        <button
          style={{
            width: '28px',
            height: '28px',
            padding: 0,
            borderRadius: '6px',
            border: '1px solid var(--bk-c-d5d7da, #d5d7da)',
            background: 'var(--bk-c-ffffff, #ffffff)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
            outline: 'none'
          }}
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
        >
          <i className={`far fa-chevron-${expanded ? 'down' : 'right'}`}
             style={{ fontSize: '14px', color: 'var(--bk-c-1982c4, #1982C4)' }}></i>
        </button>
      </div>

      {expanded && (
        <div style={{
          ...contentContainerStyle,
          padding: isMobile ? '0 12px 12px 12px' : contentContainerStyle.padding,
          WebkitOverflowScrolling: 'touch'
        }}>
          <div style={{ flex: '1 0 150px', display: 'flex', flexDirection: 'column' }}>
            {lineItems.length > 0 && (
              <div style={{
                height: 67,
                display: 'flex',
                alignItems: 'flex-end',
                marginBottom: 1
              }}>
                <div style={{ width: '14px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <label style={inputLabelStyle}>Item</label>
                </div>
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {lineItems.map((budgetLineItem, index) => (
              <SortableRow
                key={budgetLineItem.uuid || budgetLineItem.id}
                id={budgetLineItem.uuid || String(budgetLineItem.id)}
                disabled={!!budgetLineItem.isAutoGenerated || !!budgetLineItem.isReservedAncillaryIncome}
                style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative'
                }}>
                  <input
                    type="text"
                    disabled={budgetLineItem.isReservedAncillaryIncome || budgetLineItem.isAutoGenerated}
                    style={{
                      ...(budgetLineItem.isReservedAncillaryIncome || budgetLineItem.isAutoGenerated ? disabledInputStyle : inputStyle),
                      height: '100%',
                      flex: 1,
                      paddingRight: (() => {
                        const hasWarning = showsNoArtistWarning(lineItemsKey, budgetLineItem);
                        const inlineToggles = !isMobile && showInlineRowToggles &&
                          !budgetLineItem.isAutoGenerated;
                        const scissors = inlineToggles &&
                          budgetLineItem.category === 'EXPENSES' && offer?.revenue_base === 'adjusted_gross';
                        const eye = inlineToggles && showInlineVisibilityToggle &&
                          !budgetLineItem.isReservedAncillaryIncome;
                        const taxMarker = inlineToggles &&
                          canMarkAsAdmissionTax(budgetLineItem, allLineItems);
                        const exemption = inlineToggles &&
                          canToggleTaxWithholdingExemption(budgetLineItem, offer);
                        const togglesWidth = (scissors ? 26 : 0) + (eye ? 26 : 0) + (taxMarker ? 26 : 0) +
                          (exemption ? 26 : 0);
                        const extras = (showCategoryColumn ? 24 : 0) + (hasWarning ? NO_ARTIST_WARNING_WIDTH : 0) + togglesWidth;
                        return `${Math.max(8, extras)}px`;
                      })()
                    }}
                    value={budgetLineItem.name || ''}
                    onChange={(e) => !budgetLineItem.isReservedAncillaryIncome && !budgetLineItem.isAutoGenerated &&
                      handleFieldChange(budgetLineItem, 'name', e.target.value)}
                    onBlur={(e) => !budgetLineItem.isReservedAncillaryIncome && !budgetLineItem.isAutoGenerated &&
                      handleNameBlur(budgetLineItem, index, e.target.value)}
                    placeholder="Enter item label"
                    tabIndex={budgetLineItem.isReservedAncillaryIncome || budgetLineItem.isAutoGenerated ? -1 : getTabIndex(index, 'item')}
                  />
                  {showsNoArtistWarning(lineItemsKey, budgetLineItem) && (
                    <Tippy
                      content="No artist assigned. This addition won't appear on any offer until assigned."
                      placement="top"
                    >
                      <i
                        className="fas fa-exclamation-triangle"
                        style={{
                          position: 'absolute',
                          right: showCategoryColumn ? '30px' : '10px',
                          color: 'var(--bk-c-f59e0b, #F59E0B)',
                          fontSize: '14px',
                          cursor: 'help'
                        }}
                      />
                    </Tippy>
                  )}
                  {budgetLineItem.isSiblingGuarantee && (
                    <Tippy
                      content="Guarantee from a sibling offer. Click to open."
                      placement="top"
                    >
                      {/* Tippy attaches its hover/focus listeners to this
                          wrapper span so the inner <button> stays a plain
                          interactive element — fully clickable, focusable,
                          and Playwright-actionable. */}
                      <span style={{
                        position: 'absolute',
                        right: showCategoryColumn ? '30px' : '10px',
                        display: 'inline-flex',
                        lineHeight: 1
                      }}>
                        <button
                          type="button"
                          data-testid="sibling-guarantee-link"
                          aria-label="Open sibling offer"
                          onClick={(e) => {
                            if (!onSelectSiblingOffer) return;
                            e.preventDefault();
                            e.stopPropagation();
                            onSelectSiblingOffer(budgetLineItem.siblingOfferId);
                          }}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            color: 'var(--bk-c-136395, #136395)',
                            fontSize: '12px',
                            cursor: onSelectSiblingOffer ? 'pointer' : 'default',
                            lineHeight: 1
                          }}
                        >
                          <i className="fas fa-external-link-alt" aria-hidden="true" />
                        </button>
                      </span>
                    </Tippy>
                  )}
                  {!isMobile && showInlineRowToggles && !budgetLineItem.isAutoGenerated && (
                    <span style={{
                      position: 'absolute',
                      right: `${(showCategoryColumn ? 32 : 8) +
                        (showsNoArtistWarning(lineItemsKey, budgetLineItem) ? NO_ARTIST_WARNING_WIDTH : 0)}px`,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}>
                      <InlineRowToggles
                        budgetLineItem={budgetLineItem}
                        offer={offer}
                        showVisibilityToggle={showInlineVisibilityToggle}
                        sectionLineItems={allLineItems}
                        onToggle={(flag, nextValue) => handleToggleFlag(budgetLineItem, flag, nextValue)}
                      />
                    </span>
                  )}
                  {showCategoryColumn && !budgetLineItem.isAutoGenerated && (
                    <Tippy
                      content={getCategoryLabel(budgetLineItem) || 'Set category'}
                      placement="top"
                      delay={[300, 0]}
                    >
                      <i
                        ref={(el) => {
                          categoryIconRefs.current[budgetLineItem.uuid || String(budgetLineItem.id)] = el;
                        }}
                        className="fas fa-tag"
                        style={{
                          position: 'absolute',
                          right: '8px',
                          color: budgetLineItem.finance_category_id ? 'var(--bk-c-1982c4, #1982C4)' : 'var(--bk-c-d5d7da, #D5D7DA)',
                          fontSize: '18px',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const itemId = budgetLineItem.uuid || String(budgetLineItem.id);
                          setCategoryDropdownOpenId(
                            categoryDropdownOpenId === itemId ? null : itemId
                          );
                        }}
                      />
                    </Tippy>
                  )}
                </div>
              </SortableRow>
            ))}
              </SortableContext>
            </DndContext>
            <button style={{ ...addButtonStyle, padding: isMobile ? '12px 0' : addButtonStyle.padding, minHeight: isMobile ? '40px' : undefined, marginLeft: lineItems.length > 0 ? '14px' : 0, marginTop: lineItems.length > 0 ? '8px' : 0 }} onClick={handleAddRow}>
              <i className="far fa-plus-circle"></i>
              {addButtonText}
            </button>
          </div>

          {hasOfferColumn && lineItems.length > 0 && (
            showOffer ? (
              <div style={columnBoxStyle}>
                <div style={{ ...columnHeaderStyle, height: headerHeight }}>
                  <span style={columnTitleStyle}>Offer</span>
                  <button
                    style={collapseButtonStyle}
                    onClick={() => onColumnVisibilityChange &&
                      onColumnVisibilityChange('show_offer_column', false)}
                  >
                    <i className="fas fa-compress" style={{ color: 'var(--bk-c-535862, #535862)' }}></i>
                  </button>
                </div>
                <div style={{
                  display: 'flex',
                  height: labelRowHeight,
                  marginBottom: 1,
                  paddingBottom: 2
                }}>
                  <div style={{ width: calcTypeQuantityWidth, marginRight: '-1px' }}>
                    <label style={inputLabelStyle}>QTY</label>
                  </div>
                  <div style={{ width: amountWidth, marginRight: '-1px' }}>
                    <label style={inputLabelStyle}>Amount</label>
                  </div>
                  <div style={{ width: totalWidth, marginRight: showCurrencyConversion ? '-1px' : 0 }}>
                    <label style={inputLabelStyle}>Total</label>
                  </div>
                  {showCurrencyConversion && globalCurrency && (
                    <div style={{ width: convertedAmountWidth }}>
                      <label style={inputLabelStyle}>{team.secondary_currency_symbol}</label>
                    </div>
                  )}
                </div>
                {lineItems.map((budgetLineItem, index) => {
                  const calculatedRow = getCalculatedRow(budgetLineItem);
                  const effectiveCalcType = getEffectiveCalcType(budgetLineItem, 'offer');
                  const isAmountDisabled = budgetLineItem.isAutoGenerated;
                  return (
                    <div key={budgetLineItem.uuid || budgetLineItem.id}
                         style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                      <div style={{ width: calcTypeQuantityWidth, marginRight: '-1px' }}>
                        <CalcTypeQuantityInput
                          calcType={effectiveCalcType}
                          calcTypeOptions={calcTypeOptions}
                          qtyValue={isCleared(budgetLineItem, 'qty') ? '' : budgetLineItem.right_hand}
                          calculatedQtyValue={calculatedRow?.calculatedRightHand}
                          onCalcTypeChange={(newValue) => !budgetLineItem.isAutoGenerated &&
                            handleCalcTypeChange(budgetLineItem, 'offer', newValue)}
                          onQtyChange={(e) => handleQtyFieldChange(
                            budgetLineItem, '', parseNumber(e.target.value))}
                          onQtyBlur={handleQtyBlur(budgetLineItem, '', true)}
                          onQtyFocus={handleZeroFocus(budgetLineItem, 'qty', budgetLineItem.right_hand,
                            !budgetLineItem.isAutoGenerated)}
                          disabled={!!budgetLineItem.isAutoGenerated}
                          baseValuePreviews={baseValuePreviews.offer}
                          tabIndex={budgetLineItem.isAutoGenerated ? -1 : getTabIndex(index, 'offerQty')}
                        />
                      </div>
                      <div style={{
                        width: amountWidth,
                        marginRight: '-1px'
                      }}>
                        <CurrencyInput
                          disabled={isAmountDisabled}
                          style={{
                            ...(isAmountDisabled ? disabledInputStyle : inputStyle),
                            height: '100%',
                            textAlign: 'right'
                          }}
                          maskOptions={{
                            prefix: isPercentageCalcType(effectiveCalcType) ? "" : "$",
                            suffix: isPercentageCalcType(effectiveCalcType) ? "%" : "",
                            integerLimit: null,
                            allowDecimal: true,
                            decimalLimit: isPercentageCalcType(effectiveCalcType) ? 5 : (effectiveCalcType === 'per_tickets_sold' ? 4 : 2)
                          }}
                          placeholder={
                            isPercentageCalcType(effectiveCalcType) ? "0%" : "$0.00"
                          }
                          value={amountDisplayValue(budgetLineItem, '', budgetLineItem.left_hand, effectiveCalcType)}
                          onFocus={handleAmountFocus(budgetLineItem, '', budgetLineItem.left_hand, !isAmountDisabled)}
                          onChange={handleAmountInput(budgetLineItem, '', budgetLineItem.left_hand, effectiveCalcType)}
                          // blur commits 0 on the Offer column only; a blank
                          // Forecast/Actual is left empty so it keeps inheriting
                          // the Offer value rather than locking to 0.
                          onBlur={handleAmountBlur(budgetLineItem, '', !isAmountDisabled)}
                          tabIndex={isAmountDisabled ? -1 : getTabIndex(index, 'offerAmount')}
                        />
                      </div>
                      <div style={{
                        width: totalWidth,
                        marginRight: (showCurrencyConversion && globalCurrency) ? '-1px' : 0
                      }}>
                        <CurrencyInput
                          disabled
                          style={{
                            ...disabledInputStyle,
                            height: '100%',
                            textAlign: 'right'
                          }}
                          maskOptions={{ prefix: "$", integerLimit: null, allowNegative: true }}
                          placeholder="$0.00"
                          value={calculatedRow?.calculatedTotal?.toFixed(2) || ''}
                          tabIndex={-1}
                        />
                      </div>
                      {showCurrencyConversion && globalCurrency && (
                        <div style={{ width: convertedAmountWidth }}>
                          <CurrencyInput
                            disabled
                            style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                            maskOptions={{ prefix: "$", integerLimit: null, allowNegative: true }}
                            value={calculatedRow?.calculatedTotal != null
                              ? globalCurrency.convert(calculatedRow.calculatedTotal).toFixed(2)
                              : ''
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{
                  display: 'flex',
                  height: rowHeight,
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: calcTypeQuantityWidth + amountWidth,
                    marginRight: '-1px',
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px'
                  }}>
                    Total
                  </div>
                  <div style={{
                    width: totalWidth,
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px',
                    marginRight: showCurrencyConversion ? '-1px' : 0
                  }}>
                    {formatCalculatedNumber(getTotal())}
                  </div>
                  {showCurrencyConversion && globalCurrency && (
                    <div style={{
                      width: convertedAmountWidth,
                      textAlign: 'right',
                      fontWeight: '600',
                      fontSize: '14px',
                      paddingRight: '8px'
                    }}>
                      {globalCurrency.formatted(getTotal())}
                    </div>
                  )}
                </div>
                {renderPassThroughTaxRow('offer')}
              </div>
            ) : (
              <div style={offerCollapsedStyle} onClick={() => onColumnVisibilityChange &&
                onColumnVisibilityChange('show_offer_column', true)}>
                <button style={collapseButtonStyle}>
                  <i className="fas fa-expand" style={{ color: 'var(--bk-c-535862, #535862)' }}></i>
                </button>
                <span style={verticalTextStyle}>Offer</span>
              </div>
            )
          )}

          {lineItems.length > 0 && showForecast ? (
            <div style={forecastExpandedStyle}>
              <div style={{ ...columnHeaderStyle, height: headerHeight }}>
                <span style={columnTitleStyle}>Forecast</span>
                <button
                  style={collapseButtonStyle}
                  onClick={() => onColumnVisibilityChange &&
                    onColumnVisibilityChange('show_forecast_column', false)}
                >
                  <i className="fas fa-compress" style={{ color: 'var(--bk-c-136395, #136395)' }}></i>
                </button>
              </div>
              <div style={{
                display: 'flex',
                height: labelRowHeight,
                marginBottom: 1,
                  paddingBottom: 2
              }}>
                <div style={{ width: calcTypeQuantityWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>QTY</label>
                </div>
                <div style={{ width: amountWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Amount</label>
                </div>
                <div style={{ width: totalWidth, marginRight: showCurrencyConversion ? '-1px' : 0 }}>
                  <label style={inputLabelStyle}>Total</label>
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{ width: convertedAmountWidth }}>
                    <label style={inputLabelStyle}>{team.secondary_currency_symbol}</label>
                  </div>
                )}
              </div>
              {lineItems.map((budgetLineItem, index) => {
                const calculatedRow = getCalculatedRow(budgetLineItem);
                const effectiveCalcType = getEffectiveCalcType(budgetLineItem, 'forecast');
                const isAmountDisabled = budgetLineItem.isAutoGenerated;
                const forecastAmount = (budgetLineItem.left_hand_forecast != null && budgetLineItem.left_hand_forecast !== '' && parseFloat(budgetLineItem.left_hand_forecast) !== 0)
                  ? budgetLineItem.left_hand_forecast
                  : budgetLineItem.left_hand;
                return (
                  <div key={budgetLineItem.uuid || budgetLineItem.id}
                       style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                    <div style={{ width: calcTypeQuantityWidth, marginRight: '-1px' }}>
                      <CalcTypeQuantityInput
                        calcType={effectiveCalcType}
                        calcTypeOptions={calcTypeOptions}
                        qtyValue={isCleared(budgetLineItem, 'qty_forecast') ? '' : budgetLineItem.right_hand_forecast}
                        calculatedQtyValue={calculatedRow?.calculatedRightHandForecast}
                        onCalcTypeChange={(newValue) => !budgetLineItem.isAutoGenerated &&
                          handleCalcTypeChange(budgetLineItem, 'forecast', newValue)}
                        onQtyChange={(e) => handleQtyFieldChange(
                          budgetLineItem, '_forecast',
                          e.target.value.length > 0 ? e.target.value.replace(/,/g, '') : '')}
                        onQtyFocus={handleZeroFocus(budgetLineItem, 'qty_forecast', budgetLineItem.right_hand_forecast,
                          !budgetLineItem.isAutoGenerated)}
                        onQtyBlur={handleQtyBlur(budgetLineItem, '_forecast')}
                        disabled={!!budgetLineItem.isAutoGenerated}
                        baseValuePreviews={baseValuePreviews.forecast}
                        tabIndex={budgetLineItem.isAutoGenerated ? -1 : getTabIndex(index, 'forecastQty')}
                      />
                    </div>
                    <div style={{
                      width: amountWidth,
                      marginRight: '-1px'
                    }}>
                      <CurrencyInput
                        disabled={isAmountDisabled}
                        style={{
                          ...(isAmountDisabled ? disabledInputStyle : inputStyle),
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{
                          prefix: isPercentageCalcType(effectiveCalcType) ? "" : "$",
                          suffix: isPercentageCalcType(effectiveCalcType) ? "%" : "",
                          integerLimit: null,
                          allowDecimal: true,
                          decimalLimit: isPercentageCalcType(effectiveCalcType) ? 5 : (effectiveCalcType === 'per_tickets_sold' ? 4 : 2)
                        }}
                        placeholder={
                          isPercentageCalcType(effectiveCalcType) ? "0%" : "$0.00"
                        }
                        value={amountDisplayValue(budgetLineItem, '_forecast', forecastAmount, effectiveCalcType)}
                        onFocus={handleAmountFocus(budgetLineItem, '_forecast', forecastAmount, !isAmountDisabled)}
                        onBlur={handleAmountBlur(budgetLineItem, '_forecast')}
                        onChange={handleAmountInput(budgetLineItem, '_forecast', forecastAmount, effectiveCalcType)}
                        tabIndex={isAmountDisabled ? -1 : getTabIndex(index, 'forecastAmount')}
                      />
                    </div>
                    <div style={{
                      width: totalWidth,
                      marginRight: (showCurrencyConversion && globalCurrency) ? '-1px' : 0
                    }}>
                      <CurrencyInput
                        disabled
                        style={{
                          ...disabledInputStyle,
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{ prefix: "$", integerLimit: null, allowNegative: true }}
                        placeholder="$0.00"
                        value={calculatedRow?.calculatedForecastTotal?.toFixed(2) || ''}
                        tabIndex={-1}
                      />
                    </div>
                    {showCurrencyConversion && globalCurrency && (
                      <div style={{ width: convertedAmountWidth }}>
                        <CurrencyInput
                          disabled
                          style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                          maskOptions={{ prefix: "$", integerLimit: null, allowNegative: true }}
                          value={calculatedRow?.calculatedForecastTotal != null
                            ? globalCurrency.convert(calculatedRow.calculatedForecastTotal).toFixed(2)
                            : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{
                display: 'flex',
                height: rowHeight,
                alignItems: 'center'
              }}>
                <div style={{
                  width: calcTypeQuantityWidth + amountWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  Total
                </div>
                <div style={{
                  width: totalWidth,
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px',
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  {formatCalculatedNumber(getForecastTotal())}
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{
                    width: convertedAmountWidth,
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px'
                  }}>
                    {globalCurrency.formatted(getForecastTotal())}
                  </div>
                )}
              </div>
              {renderPassThroughTaxRow('forecast')}
            </div>
          ) : lineItems.length > 0 && !hideCollapsedColumns ? (
            <div style={forecastCollapsedStyle} onClick={() => onColumnVisibilityChange &&
              onColumnVisibilityChange('show_forecast_column', true)}>
              <button style={collapseButtonStyle}>
                <i className="fas fa-expand" style={{ color: 'var(--bk-c-136395, #136395)' }}></i>
              </button>
              <span style={verticalTextStyle}>Forecast</span>
            </div>
          ) : null}

          {lineItems.length > 0 && showActuals ? (
            <div style={actualsExpandedStyle}>
              <div style={{ ...columnHeaderStyle, height: headerHeight }}>
                <span style={columnTitleStyle}>Actuals</span>
                <button
                  style={collapseButtonStyle}
                  onClick={() => onColumnVisibilityChange &&
                    onColumnVisibilityChange('show_actuals_column', false)}
                >
                  <i className="fas fa-compress" style={{ color: 'var(--bk-c-155eef, #155EEF)' }}></i>
                </button>
              </div>
              <div style={{
                display: 'flex',
                height: labelRowHeight,
                marginBottom: 1,
                  paddingBottom: 2
              }}>
                <div style={{ width: calcTypeQuantityWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>QTY</label>
                </div>
                <div style={{ width: amountWidth, marginRight: '-1px' }}>
                  <label style={inputLabelStyle}>Amount</label>
                </div>
                <div style={{ width: totalWidth, marginRight: showCurrencyConversion ? '-1px' : 0 }}>
                  <label style={inputLabelStyle}>Total</label>
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{ width: convertedAmountWidth }}>
                    <label style={inputLabelStyle}>{team.secondary_currency_symbol}</label>
                  </div>
                )}
              </div>
              {lineItems.map((budgetLineItem, index) => {
                const calculatedRow = getCalculatedRow(budgetLineItem);
                const effectiveCalcType = getEffectiveCalcType(budgetLineItem, 'actual');
                const isPerTicketsSold = effectiveCalcType === 'per_tickets_sold';
                const isReserved = budgetLineItem.isReservedAncillaryIncome;
                const isLift = budgetLineItem.isAutoGenerated;
                const isActualsAmountDisabled = isReserved || isLift;
                const hasForecastLeft = budgetLineItem.left_hand_forecast != null && budgetLineItem.left_hand_forecast !== '' && parseFloat(budgetLineItem.left_hand_forecast) !== 0;
                const hasActualLeft = budgetLineItem.left_hand_actual != null && budgetLineItem.left_hand_actual !== '' && parseFloat(budgetLineItem.left_hand_actual) !== 0;
                const actualAmount = isReserved || isLift
                  ? (budgetLineItem.left_hand_actual || budgetLineItem.left_hand || 0)
                  : isPerTicketsSold
                    ? (hasActualLeft
                        ? budgetLineItem.left_hand_actual
                        : (hasForecastLeft ? budgetLineItem.left_hand_forecast : budgetLineItem.left_hand || ''))
                    : hasActualLeft
                        ? budgetLineItem.left_hand_actual
                        : (budgetLineItem.left_hand_forecast || 0);
                return (
                  <div key={budgetLineItem.uuid || budgetLineItem.id}
                       style={{ display: 'flex', height: rowHeight, marginTop: '-1px' }}>
                    <div style={{ width: calcTypeQuantityWidth, marginRight: '-1px' }}>
                      <CalcTypeQuantityInput
                        calcType={effectiveCalcType}
                        calcTypeOptions={calcTypeOptions}
                        qtyValue={isCleared(budgetLineItem, 'qty_actual') ? '' : budgetLineItem.right_hand_actual}
                        calculatedQtyValue={calculatedRow?.calculatedRightHandActual}
                        onCalcTypeChange={(newValue) => !budgetLineItem.isAutoGenerated &&
                          handleCalcTypeChange(budgetLineItem, 'actual', newValue)}
                        onQtyChange={(e) => handleQtyFieldChange(
                          budgetLineItem, '_actual',
                          e.target.value.length > 0 ? e.target.value.replace(/,/g, '') : '')}
                        onQtyFocus={handleZeroFocus(budgetLineItem, 'qty_actual', budgetLineItem.right_hand_actual,
                          !(isReserved || isLift))}
                        onQtyBlur={handleQtyBlur(budgetLineItem, '_actual')}
                        disabled={!!(isReserved || isLift)}
                        baseValuePreviews={baseValuePreviews.actual}
                        tabIndex={(isReserved || isLift) ? -1 : getTabIndex(index, 'actualsQty')}
                      />
                    </div>
                    <div style={{
                      width: amountWidth,
                      marginRight: '-1px'
                    }}>
                      <CurrencyInput
                        disabled={isActualsAmountDisabled}
                        style={{
                          ...(isActualsAmountDisabled ? disabledInputStyle : inputStyle),
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{
                          prefix: isPercentageCalcType(effectiveCalcType) ? "" : "$",
                          suffix: isPercentageCalcType(effectiveCalcType) ? "%" : "",
                          integerLimit: null,
                          allowDecimal: true,
                          decimalLimit: isPercentageCalcType(effectiveCalcType) ? 5 : (effectiveCalcType === 'per_tickets_sold' ? 4 : 2)
                        }}
                        placeholder={
                          isPercentageCalcType(effectiveCalcType) ? "0%" : "$0.00"
                        }
                        value={amountDisplayValue(budgetLineItem, '_actual', actualAmount, effectiveCalcType)}
                        onFocus={handleAmountFocus(budgetLineItem, '_actual', actualAmount, !isActualsAmountDisabled)}
                        onBlur={handleAmountBlur(budgetLineItem, '_actual')}
                        onChange={handleAmountInput(budgetLineItem, '_actual', actualAmount, effectiveCalcType)}
                        tabIndex={isActualsAmountDisabled ? -1 : getTabIndex(index, 'actualsAmount')}
                      />
                    </div>
                    <div style={{
                      width: totalWidth,
                      marginRight: (showCurrencyConversion && globalCurrency) ? '-1px' : 0
                    }}>
                      <CurrencyInput
                        disabled
                        style={{
                          ...disabledInputStyle,
                          height: '100%',
                          textAlign: 'right'
                        }}
                        maskOptions={{ prefix: "$", integerLimit: null, allowNegative: true }}
                        placeholder="$0.00"
                        value={calculatedRow?.calculatedActualTotal?.toFixed(2) || ''}
                        tabIndex={-1}
                      />
                    </div>
                    {showCurrencyConversion && globalCurrency && (
                      <div style={{ width: convertedAmountWidth }}>
                        <CurrencyInput
                          disabled
                          style={{ ...disabledInputStyle, height: '100%', textAlign: 'right' }}
                          maskOptions={{ prefix: "$", integerLimit: null, allowNegative: true }}
                          value={calculatedRow?.calculatedActualTotal != null
                            ? globalCurrency.convert(calculatedRow.calculatedActualTotal).toFixed(2)
                            : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{
                display: 'flex',
                height: rowHeight,
                alignItems: 'center'
              }}>
                <div style={{
                  width: calcTypeQuantityWidth + amountWidth,
                  marginRight: '-1px',
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px'
                }}>
                  Total
                </div>
                <div style={{
                  width: totalWidth,
                  textAlign: 'right',
                  fontWeight: '600',
                  fontSize: '14px',
                  paddingRight: '8px',
                  marginRight: showCurrencyConversion ? '-1px' : 0
                }}>
                  {formatCalculatedNumber(getActualsTotal())}
                </div>
                {showCurrencyConversion && globalCurrency && (
                  <div style={{
                    width: convertedAmountWidth,
                    textAlign: 'right',
                    fontWeight: '600',
                    fontSize: '14px',
                    paddingRight: '8px'
                  }}>
                    {globalCurrency.formatted(getActualsTotal())}
                  </div>
                )}
              </div>
              {renderPassThroughTaxRow('actual')}
            </div>
          ) : lineItems.length > 0 && !hideCollapsedColumns ? (
            <div style={actualsCollapsedStyle} onClick={() => onColumnVisibilityChange &&
              onColumnVisibilityChange('show_actuals_column', true)}>
              <button style={collapseButtonStyle}>
                <i className="fas fa-expand" style={{ color: 'var(--bk-c-155eef, #155EEF)' }}></i>
              </button>
              <span style={verticalTextStyle}>Actuals</span>
            </div>
          ) : null}

          {lineItems.length > 0 && (
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: isMobile ? '64px' : '14px',
            alignItems: 'center'
          }}>
            <div style={{ height: totalHeaderHeight }}></div>
            {lineItems.map((budgetLineItem) => (
              <div key={budgetLineItem.uuid || budgetLineItem.id}
                   style={{
                     height: rowHeight,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     marginTop: '-1px'
                   }}>
                {!budgetLineItem.isAutoGenerated && (
                <LineItemRowMenu
                  offer={offer}
                  showEditDetails={showEditDetails}
                  budgetLineItem={budgetLineItem}
                  onDeleteRow={handleDeleteRow}
                  onEditDetails={(item) => setDetailsPaneBudgetLineItem({...item})}
                  allowOfferVisibilityToggle={allowOfferVisibilityToggle}
                  sectionLineItems={allLineItems}
                  onToggleFlag={handleToggleFlag}
                />
              )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {renderCategoryDropdown()}

      <BudgetLineItemDetailsPane
        financeCategories={financeCategories}
        isOpen={detailsPaneBudgetLineItem !== null}
        onClose={() => setDetailsPaneBudgetLineItem(null)}
        budgetLineItem={detailsPaneBudgetLineItem || {}}
        csrfToken={csrfToken}
        team={team}
        offer={offer}
        budget={budget}
        updateBudget={updateBudget}
        updatedBudgetWithBudgetLineItem={updatedBudgetWithBudgetLineItem}
        performers={performers}
      />
    </div>
  );
};

BudgetLineItemSection.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  showEditDetails: PropTypes.bool,
  offer: PropTypes.object.isRequired,
  budget: PropTypes.object.isRequired,
  calculated: PropTypes.object,
  updateBudget: PropTypes.func.isRequired,
  onPassThroughChange: PropTypes.func,
  showInlineVisibilityToggle: PropTypes.bool,
  createBudgetLineItem: PropTypes.func.isRequired,
  deleteBudgetLineItem: PropTypes.func.isRequired,
  autoSaveBudgetLineItem: PropTypes.func.isRequired,
  formatCalculatedNumber: PropTypes.func.isRequired,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  sectionTitle: PropTypes.string.isRequired,
  sectionSubtitle: PropTypes.string,
  lineItemsKey: PropTypes.string.isRequired,
  lineItemsFilter: PropTypes.func,
  calculatedSectionKey: PropTypes.string.isRequired,
  calculatedTotalSectionKey: PropTypes.string,
  calculatedTotalKey: PropTypes.string,
  calculatedForecastTotalKey: PropTypes.string,
  calculatedActualTotalKey: PropTypes.string,
  calculatedRightTotalKey: PropTypes.string,
  calculatedRightForecastTotalKey: PropTypes.string,
  calculatedRightActualTotalKey: PropTypes.string,
  addButtonText: PropTypes.string.isRequired,
  hasOfferColumn: PropTypes.bool,
  showOfferColumn: PropTypes.bool,
  showForecastColumn: PropTypes.bool,
  showActualsColumn: PropTypes.bool,
  hideCollapsedColumns: PropTypes.bool,
  allowOfferVisibilityToggle: PropTypes.bool,
  showInlineRowToggles: PropTypes.bool,
  onColumnVisibilityChange: PropTypes.func,
  financeCategories: PropTypes.array,
  onFinanceCategoryAdded: PropTypes.func,
  performers: PropTypes.array,
  sectionTabIndexOffset: PropTypes.number,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object,
  actualsItems: PropTypes.object
};

export default BudgetLineItemSection;

